import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { enquiryInputType, roles } from '@constants'
import { FormWrapper } from './styles'
import CategoryHsnRow from './CategoryHsnRow'
import SimpleProductFields from './SimpleProductFields'
import SimpleProductTable from './SimpleProductTable'
import TmtProductTable from './TmtProductTable'
import MessageConsentSection from './MessageConsentSection'
import { buildUnknownProductRow } from './inputAdapters'

const EnquiryItemForm = React.memo(({ enquiryItems, itemId, state, updateState, defaultBrand, defualtCategoryId, checkTmtItem, activeCommodity, productsLoading }) => {
	const { category, product, requiredWeight, targetPrice, brands, message, tcRequired, inputType, grade, hsn_code } = state
	const [tmtProduct, setTmtProduct] = useState()
	const user = useSelector((state) => state.getIn(['user', 'user']))
	const [preferredUnits, setPrefferedUnits] = useState([])
	const gradeRowCounter = useRef(0)
	const unknownRowCounter = useRef(0)
	// Always points at the latest values so the callbacks below can stay referentially
	// stable (empty/const deps) without reading stale state through their closures.
	const latestRef = useRef({})
	latestRef.current = { tmtProduct, inputType, requiredWeight, defaultBrand }
	useEffect(() => {
		if (!product?.tmtProduct) {
			let updateTmtValue = product?.data.map((item, index) => {
				item.required_weight = ''
				item.grade = ''
				item.message = ''
				item._rowId = item.product_id ?? `base-${index}`
				return item
			})
			setTmtProduct(updateTmtValue)
			if (product?.value?.product_id?.includes('scrap')) {
				updateState('brands', { value: [defaultBrand], disabled: true, dirty: true })
			}
		}
		const filteredUnits = Object.keys(enquiryInputType).filter((key) => state.category.value?.preferred_units?.includes(key))
		setPrefferedUnits(filteredUnits.map((key) => ({ key: key, label: enquiryInputType[key] })))
	}, [product])

	useEffect(() => {
		if (preferredUnits === null || preferredUnits.length === 0) {
			updateState('inputType', { value: inputType?.value ? inputType?.value : '' })
		} else {
			updateState('inputType', { value: inputType?.value ? inputType?.value : preferredUnits?.[0]?.key })
		}
	}, [preferredUnits])

	useEffect(() => {
		if (user?.role === roles.manufacturer) {
			updateState('category', { value: category.data.find((_c) => _c.category_id === 'scrap'), dirty: true, disabled: true })
			updateState('brands', { value: [defaultBrand], disabled: true, dirty: true })
		} else if (defualtCategoryId?.includes('scrap')) {
			updateState('brands', { value: [defaultBrand], disabled: true, dirty: true })
		}
	}, [user, category.data, defualtCategoryId])

	const resetFormBasedOnCategory = useCallback(() => {
		updateState('brands', { value: [], disabled: false, dirty: true })
		updateState('requiredWeight', { value: '', dirty: true })
		updateState('targetPrice', { value: '', dirty: true })
		updateState('inputType', { value: '', dirty: true })
		updateState('grade', { value: '', dirty: true })
		updateState('message', { value: '', dirty: true })
		updateState('tcRequired', { value: true, dirty: true })
	}, [updateState])

	// `onChangeRef` lets the stable callback recurse into itself without adding itself as a dep.
	const onChangeRef = useRef()
	const onChange = useCallback(
		(key) => (value) => {
			const { inputType: it, requiredWeight: rw, defaultBrand: db } = latestRef.current
			if (it.value === enquiryInputType.WEIGHT && key === enquiryInputType.WEIGHT) {
				value = rw?.value * 1000
			}

			if (key === 'category') {
				if (value?.category_id?.includes('scrap')) {
					updateState('brands', { value: [db], disabled: true, dirty: true })
				} else {
					resetFormBasedOnCategory()
				}
				updateState('hsn_code', { value: value?.hsn_code ? value?.hsn_code : '', disabled: true, dirty: true })
			}

			if (key === 'inputType') {
				value = value?.key
				onChangeRef.current('requiredWeight')('')
			}

			updateState(key, { value, dirty: true })
		},
		[updateState, resetFormBasedOnCategory]
	)
	onChangeRef.current = onChange

	const onCheckBoxChange = (e) => onChange('tcRequired')(e.target.checked)

	// Immutable per-cell update: replaces only the edited row with a new object inside a new
	// array. Keeping every other row's reference intact lets memoized rows skip re-rendering,
	// and the fresh array reference is what lets the memoized table see the change.
	const tmtProductHandler = useCallback(
		(tmtProd, index) => {
			const next = [...(latestRef.current.tmtProduct ?? [])]
			next[index] = tmtProd
			setTmtProduct(next)
			updateState('product', { tmtProduct: next, dirty: true })
		},
		[updateState]
	)

	// Inserts a new blank grade row directly after the product's existing group of rows.
	const addGradeRow = useCallback(
		(index) => {
			const current = latestRef.current.tmtProduct
			if (!current) return
			const base = current[index]
			let insertAt = index + 1
			while (insertAt < current.length && current[insertAt]?.product_id === base?.product_id) {
				insertAt++
			}
			gradeRowCounter.current += 1
			const newRow = {
				...base,
				grade: '',
				required_weight: '',
				target_price: '',
				message: '',
				is_added_grade: true,
				_rowId: `${base?.product_id ?? 'row'}-g${gradeRowCounter.current}`,
			}
			const next = [...current]
			next.splice(insertAt, 0, newRow)
			setTmtProduct(next)
			updateState('product', { tmtProduct: next, dirty: true })
		},
		[updateState]
	)

	const removeGradeRow = useCallback(
		(index) => {
			const current = latestRef.current.tmtProduct
			if (!current) return
			const next = current.filter((_, i) => i !== index)
			setTmtProduct(next)
			updateState('product', { tmtProduct: next, dirty: true })
		},
		[updateState]
	)

	// Appends a row for a product not present in the category's product list, keyed by
	// the typed search text instead of a real product_id (mirrors the old creatable-Select flow).
	const addUnknownProductRow = useCallback(
		(name) => {
			const current = latestRef.current.tmtProduct ?? []
			unknownRowCounter.current += 1
			const newRow = buildUnknownProductRow(name, `unknown-${unknownRowCounter.current}`)
			const next = [...current, newRow]
			setTmtProduct(next)
			updateState('product', { tmtProduct: next, dirty: true })
		},
		[updateState]
	)

	const isTmtItem = checkTmtItem.find((item) => item === category?.value?.category_id) && !itemId

	return (
		<FormWrapper>
			<CategoryHsnRow category={category} hsn_code={hsn_code} onChange={onChange} />
			{itemId ? (
				<SimpleProductFields
					product={product}
					brands={brands}
					requiredWeight={requiredWeight}
					inputType={inputType}
					preferredUnits={preferredUnits}
					targetPrice={targetPrice}
					grade={grade}
					activeCommodity={activeCommodity}
					onChange={onChange}
					user={user}
				/>
			) : isTmtItem ? (
				<TmtProductTable
					brands={brands}
					inputType={inputType}
					preferredUnits={preferredUnits}
					grade={grade}
					targetPrice={targetPrice}
					tmtProduct={tmtProduct}
					enquiryItems={enquiryItems}
					onChange={onChange}
					tmtProductHandler={tmtProductHandler}
					addGradeRow={addGradeRow}
					removeGradeRow={removeGradeRow}
					addUnknownProductRow={addUnknownProductRow}
					productsLoading={productsLoading}
					user={user}
				/>
			) : (
				<SimpleProductTable
					category={category}
					brands={brands}
					inputType={inputType}
					preferredUnits={preferredUnits}
					grade={grade}
					targetPrice={targetPrice}
					requiredWeight={requiredWeight}
					tmtProduct={tmtProduct}
					enquiryItems={enquiryItems}
					activeCommodity={activeCommodity}
					onChange={onChange}
					tmtProductHandler={tmtProductHandler}
					addGradeRow={addGradeRow}
					removeGradeRow={removeGradeRow}
					addUnknownProductRow={addUnknownProductRow}
					productsLoading={productsLoading}
					user={user}
				/>
			)}
			<MessageConsentSection
				message={message}
				tcRequired={tcRequired}
				onChange={onChange}
				onCheckBoxChange={onCheckBoxChange}
				showMessage={Boolean(itemId)}
			/>
		</FormWrapper>
	)
})

export default EnquiryItemForm
